import React, { useState, useEffect, useCallback } from 'react';

const Testimonials = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Move testimonials array to the top before any hooks that use it
  const testimonials = [
    // Early Success Stories
    {
      id: 1,
      initials: "CM",
      name: "Chimwemwe Banda",
      location: "Lilongwe, Malawi",
      earnings: "MK 45,200 first month",
      duration: "Founding Member",
      text: "I joined VumaQash right after launch and it's been incredible! In just weeks I've made more than my part-time job. The community support is amazing!",
      image: "👨‍🎓",
      bgColor: "from-red-500 to-green-500",
      flag: "🇲🇼",
      verified: true,
      streams: ["Direct Referral", "Team Building", "Daily Login"],
      rating: 5,
      joinDate: "September 2025"
    },
    {
      id: 2,
      name: "Sarah Johnson",
      location: "Lusaka, Zambia",
      earnings: "$220 in 2 weekSs",
      duration: "Early Adopter",
      text: "As one of the first members, I've seen this platform grow rapidly. The earnings are real and the team is constantly adding new features!",
      image: "👩‍💼",
      bgColor: "from-green-500 to-blue-500",
      flag: "🇿🇲",
      verified: true,
      streams: ["Direct Referral", "Residual Income", "Bonuses"],
      rating: 5,
      joinDate: "September 2025"
    },
    {
      id: 3,
      name: "David Kimani",
      location: "Nairobi, Kenya",
      earnings: "Ksh 18,500 first week",
      duration: "Launch Member",
      text: "I was skeptical at first, but joining right after launch was the best decision. The platform is fresh and the opportunities are massive!",
      image: "💼",
      bgColor: "from-black to-red-500",
      flag: "🇰🇪",
      verified: true,
      streams: ["Team Building", "Leadership", "Daily Rewards"],
      rating: 5,
      joinDate: "September 2025"
    },
    {
      id: 4,
      name: "Amina Mohammed",
      location: "Dar es Salaam, Tanzania",
      earnings: "TZS 320,000 total",
      duration: "Founding Member",
      text: "Being part of this from the beginning has been rewarding. The simplicity and daily earnings make it perfect for beginners!",
      image: "👩‍🎓",
      bgColor: "from-blue-500 to-green-500",
      flag: "🇹🇿",
      verified: true,
      streams: ["Direct Referral", "Activity Bonus", "Team Growth"],
      rating: 5,
      joinDate: "September 2025"
    }
  ];

  const countryStats = [
    { 
      country: "Malawi", 
      members: "247+", 
      avgEarnings: "MK 38,000 avg", 
      flag: "🇲🇼", 
      color: "from-red-500 to-green-500",
      growth: "Growing daily"
    },
    { 
      country: "Kenya", 
      members: "189+", 
      avgEarnings: "Ksh 15,200 avg", 
      flag: "🇰🇪", 
      color: "from-black to-red-500",
      growth: "Rapid expansion"
    },
    { 
      country: "Zambia", 
      members: "156+", 
      avgEarnings: "$180 avg", 
      flag: "🇿🇲", 
      color: "from-green-500 to-red-500",
      growth: "New members daily"
    }
  ];

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const nextTestimonial = useCallback(() => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, [testimonials.length]);

  const prevTestimonial = useCallback(() => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, [testimonials.length]);

  const StarRating = ({ rating }) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <span 
            key={star} 
            className={`text-xl ${star <= rating ? 'text-amber-400' : 'text-gray-300'}`}
          >
            ⭐
          </span>
        ))}
      </div>
    );
  };

  // Safe testimonial access
  const currentTestimonial = testimonials[activeTestimonial] || testimonials[0];

  return (
    <section 
      className={`relative py-20 lg:py-32 overflow-hidden bg-gradient-to-br from-white via-blue-50 to-purple-50 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      id="testimonials"
    >
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-200/30 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-200/30 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-amber-200/20 rounded-full blur-3xl"></div>
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center space-x-4 bg-white text-blue-600 px-8 py-4 rounded-2xl text-lg font-bold mb-8 shadow-2xl shadow-blue-100 border border-blue-100 transform hover:scale-105 transition-all duration-500">
            <span className="h-3 w-3 bg-green-500 rounded-full animate-pulse shadow-lg shadow-green-400"></span>
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent font-black tracking-wide">
              LAUNCH SUCCESS STORIES
            </span>
            <span className="h-3 w-3 bg-purple-500 rounded-full animate-pulse shadow-lg shadow-purple-400" style={{animationDelay: '700ms'}}></span>
          </div>
          
          <h2 className="text-5xl md:text-7xl font-black mb-8 leading-tight">
            <span className="bg-gradient-to-r from-gray-900 via-blue-700 to-purple-700 bg-clip-text text-transparent">
              First Members,
            </span>
            <span className="block mt-4 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Amazing Results
            </span>
          </h2>
          
          <p className="text-2xl md:text-3xl text-gray-600 max-w-4xl mx-auto leading-relaxed font-light mb-6">
            Join <span className="font-semibold text-blue-600">592+ founding members</span> who started with us in September 2025
          </p>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            Real results from our first month of operation. Be part of our growth story from the beginning!
          </p>

          {/* Launch Badge */}
          <div className="mt-8 inline-flex items-center space-x-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
            <span>🚀</span>
            <span>OFFICIALLY LAUNCHED SEPTEMBER 13, 2025</span>
            <span>🎉</span>
          </div>
        </div>

        {/* Country Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20 max-w-5xl mx-auto">
          {countryStats.map((stat, index) => (
            <div 
              key={stat.country}
              className="group relative bg-white backdrop-blur-sm rounded-3xl p-8 text-center shadow-2xl shadow-gray-200/60 border-2 border-gray-100 transform transition-all duration-500 hover:scale-105 hover:shadow-2xl overflow-hidden"
            >
              {/* Shine Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              
              <div className="relative z-10">
                <div className="text-6xl mb-6 drop-shadow-lg transform group-hover:scale-110 transition-transform duration-300">{stat.flag}</div>
                <h3 className="text-3xl font-black text-gray-900 mb-4 drop-shadow-sm">{stat.country}</h3>
                <p className="text-2xl font-bold text-blue-600 mb-3">{stat.members}</p>
                <p className="text-gray-700 font-semibold text-lg mb-2">{stat.avgEarnings}</p>
                <p className="text-green-600 font-bold text-sm">{stat.growth}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Main Testimonial Carousel */}
        <div className="max-w-6xl mx-auto mb-16">
          <div className="relative bg-white backdrop-blur-sm rounded-3xl border-2 border-gray-200 shadow-2xl shadow-gray-300/30 overflow-hidden transform transition-all duration-500 hover:shadow-2xl">
            {/* Navigation Arrows */}
            <button 
              onClick={prevTestimonial}
              className="absolute left-6 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white shadow-2xl hover:scale-110 transition-all duration-300 z-20 group border-2 border-white"
              aria-label="Previous testimonial"
            >
              <span className="text-2xl group-hover:-translate-x-0.5 transition-transform">←</span>
            </button>
            
            <button 
              onClick={nextTestimonial}
              className="absolute right-6 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white shadow-2xl hover:scale-110 transition-all duration-300 z-20 group border-2 border-white"
              aria-label="Next testimonial"
            >
              <span className="text-2xl group-hover:translate-x-0.5 transition-transform">→</span>
            </button>

            {/* Testimonial Content */}
            <div className="p-8 md:p-12">
              <div className="flex flex-col lg:flex-row items-center gap-12">
                {/* Avatar Section */}
                <div className="flex-shrink-0">
                  <div className="relative group">
                    <div className={`w-40 h-40 bg-gradient-to-r ${currentTestimonial.bgColor} rounded-3xl flex items-center justify-center text-white text-5xl font-bold shadow-2xl transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 border-4 border-white`}>
                      {currentTestimonial.image}
                    </div>
                    
                    {/* Verified Badge */}
                    {currentTestimonial.verified && (
                      <div className="absolute -top-3 -right-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm px-4 py-2 rounded-full flex items-center space-x-2 shadow-lg border-2 border-white">
                        <span className="text-lg">✓</span>
                        <span className="font-black">Verified</span>
                      </div>
                    )}
                    
                    {/* Country Flag */}
                    <div className="absolute -bottom-3 -left-3 bg-white rounded-full p-3 shadow-lg border-2 border-gray-200">
                      <span className="text-3xl">{currentTestimonial.flag}</span>
                    </div>

                    {/* Join Date */}
                    <div className="absolute -bottom-2 right-4 bg-white/90 backdrop-blur-sm text-gray-700 text-xs px-3 py-1 rounded-full border border-gray-300 font-semibold">
                      Joined {currentTestimonial.joinDate}
                    </div>
                  </div>
                </div>

                {/* Content Section */}
                <div className="flex-1 text-center lg:text-left">
                  {/* Header */}
                  <div className="mb-8">
                    <h3 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
                      {currentTestimonial.name}
                    </h3>
                    <p className="text-blue-600 text-xl font-semibold mb-4">{currentTestimonial.location}</p>
                    
                    {/* Earnings Highlight */}
                    <div className="inline-flex items-center space-x-4 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-2xl px-6 py-3 mb-4 shadow-lg">
                      <span className="text-amber-600 text-2xl">💰</span>
                      <span className="text-amber-700 font-black text-xl">{currentTestimonial.earnings}</span>
                      <span className="text-amber-600 font-semibold">{currentTestimonial.duration}</span>
                    </div>
                  </div>

                  {/* Testimonial Text */}
                  <blockquote className="text-2xl text-gray-700 leading-relaxed mb-8 font-light italic bg-gray-50/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-200">
                    "{currentTestimonial.text}"
                  </blockquote>

                  {/* Rating */}
                  <div className="flex items-center justify-center lg:justify-start space-x-4 mb-8">
                    <StarRating rating={currentTestimonial.rating} />
                    <span className="text-gray-600 font-semibold text-lg">5.0 Rating</span>
                  </div>

                  {/* Active Income Streams */}
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 backdrop-blur-sm rounded-2xl p-6 border-2 border-blue-200 shadow-lg">
                    <p className="text-green-700 font-black text-lg mb-4 text-center lg:text-left">Active Income Streams:</p>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                      {currentTestimonial.streams.map((stream, i) => (
                        <span 
                          key={i} 
                          className="bg-white text-cyan-700 text-sm font-semibold px-4 py-3 rounded-xl border-2 border-cyan-200 transform transition-all duration-300 hover:scale-105 hover:shadow-lg text-center"
                        >
                          {stream}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="h-2 bg-gray-200">
              <div 
                className="h-full bg-gradient-to-r from-amber-400 to-cyan-400 transition-all duration-5000 ease-linear shadow-lg"
                style={{ 
                  width: isAutoPlaying ? '100%' : '0%',
                  transition: isAutoPlaying ? 'width 5s linear' : 'none'
                }}
              ></div>
            </div>
          </div>
        </div>

        {/* Testimonial Dots */}
        <div className="flex justify-center space-x-4 mb-16">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setActiveTestimonial(index);
                setIsAutoPlaying(false);
                setTimeout(() => setIsAutoPlaying(true), 10000);
              }}
              className={`w-4 h-4 rounded-full transition-all duration-300 border-2 ${
                index === activeTestimonial 
                  ? 'bg-gradient-to-r from-amber-400 to-cyan-400 scale-125 border-white shadow-lg' 
                  : 'bg-gray-300 border-gray-400 hover:bg-gray-400'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>

        {/* Enhanced CTA Section */}
        <div className="text-center">
          <div className="bg-white backdrop-blur-xl rounded-3xl p-12 max-w-4xl mx-auto relative overflow-hidden border-2 border-gray-200 shadow-2xl shadow-gray-300/30">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-cyan-50 to-purple-50"></div>
            
            <div className="relative z-10">
              <h3 className="text-4xl md:text-5xl font-black text-gray-900 mb-8">
                Join Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Launch Team</span>
              </h3>
              <p className="text-2xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed font-light">
                Get in early and build with us from the ground up. <strong className="text-blue-600">Maximum opportunities for founding members!</strong>
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-10">
                <a 
                  href="https://wa.me/254115272510?text=I%20want%20to%20join%20VumaQash%20as%20a%20founding%20member" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-black text-lg py-5 px-10 rounded-2xl transition-all duration-300 shadow-2xl shadow-green-500/30 hover:shadow-green-500/50 hover:scale-105 flex items-center space-x-4 border-2 border-white"
                >
                  <span className="text-2xl">💬</span>
                  <span>Join Founding Team</span>
                  <span className="group-hover:translate-x-2 transition-transform duration-300">→</span>
                </a>
                
                <a 
                  href="https://www.vumaqash.com/user/register.php?ref=CLEOPHAS"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-black text-lg py-5 px-10 rounded-2xl transition-all duration-300 shadow-2xl shadow-amber-500/30 hover:shadow-amber-500/50 hover:scale-105 flex items-center space-x-4 border-2 border-white"
                >
                  <span className="text-2xl">🚀</span>
                  <span>Register Now</span>
                  <span className="group-hover:translate-x-2 transition-transform duration-300">→</span>
                </a>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-gray-600">
                {[
                  { icon: "⚡", text: "Founding Benefits", color: "text-amber-500" },
                  { icon: "📱", text: "Easy Start", color: "text-blue-500" },
                  { icon: "💰", text: "Daily Earnings", color: "text-green-500" },
                  { icon: "🛡️", text: "Launch Support", color: "text-purple-500" }
                ].map((feature, index) => (
                  <div key={index} className="flex flex-col items-center space-y-2 group transform hover:scale-105 transition-all duration-300">
                    <span className={`text-3xl ${feature.color} group-hover:scale-110 transition-transform duration-300`}>{feature.icon}</span>
                    <span className="font-semibold text-gray-700 text-center">{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Trust Statistics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-20 max-w-6xl mx-auto">
          {[
            { number: "592+", label: "Founding Members", icon: "👥", color: "from-blue-500 to-cyan-500" },
            { number: "100%", label: "Satisfaction", icon: "😊", color: "from-green-500 to-emerald-500" },
            { number: "14", label: "Days Active", icon: "📅", color: "from-amber-500 to-yellow-500" },
            { number: "6", label: "Countries", icon: "🌍", color: "from-purple-500 to-pink-500" }
          ].map((stat, index) => (
            <div 
              key={stat.label}
              className="group relative bg-white backdrop-blur-sm rounded-2xl p-8 text-center border-2 border-gray-200 shadow-xl shadow-gray-200/50 transform transition-all duration-500 hover:scale-105 hover:shadow-2xl"
            >
              <div className={`text-4xl mb-4 bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                {stat.icon}
              </div>
              <div className="text-3xl font-black text-gray-900 mb-2">{stat.number}</div>
              <div className="text-gray-600 font-semibold">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;